import { useSearchParams } from 'react-router-dom'

import { useTranslation } from 'react-i18n-lite'

import {
  Button,
  DocumentListCard,
  SyntheticButton,
  YesObject,
  feedbackcard,
  useDisplayUserError
} from '@yes.technology/react-toolkit'

import usePersistentSelection from 'site/Renderer/components/FilterFields/hooks/usePersistentSelection'

import useSelection from '../../hooks/useSelection'
import useTableActionSave from '../../hooks/useTableActionSave/useTableActionSave'
import useTableContext from '../../hooks/useTableContext'

import InsertButton from './InsertButton'

import { useSiteState } from 'siteState/shared'
import { ButtonContainer, ButtonList, Container } from './TableActions.styles'
import { TableActionsProps } from './TableActions.types'
import { SelectionAction } from '../../types'
import { useMemo } from 'react'

// hide select and deselect fields until implementation
const SHOW_SELECT_DESELECT = false

const TableActions = ({ initialStatus }: TableActionsProps) => {
  const { selectAllRows, selectedRows, deselectAllRows } = useSelection()
  const { t } = useTranslation()
  const {
    mode,
    setMode,
    setChangedFieldValues,
    selectAndLinkOptions,
    objectclass,
    primarySelectionAction,
    hideEditAction,
    extraSelectionActions,
    disableLinkButton
  } = useTableContext()

  const {
    selectAndLink,
    shouldRenderSelectAndLink,
    getRedirectToObjectclassCallback
  } = usePersistentSelection()

  const { displayUserError } = useDisplayUserError()

  const [_, setRefetchObjectView] = useSiteState<boolean>('refetch-objectview')

  const [searchParams] = useSearchParams()
  const isUniselection = searchParams.has('uniselection')

  const save = useTableActionSave({
    initialStatusUuid: initialStatus?.uuid || ''
  })

  const onSave = async () => {
    const result = await save()

    const isSuccessful = result?.every((obj) => obj?.success)

    if (isSuccessful) {
      feedbackcard(t('data-tables.success'), {
        type: 'success',
        message: t('data-tables.success')
      })

      setRefetchObjectView(true)

      return
    }

    const error = result?.find((obj) => !obj?.success)

    if (!error || error?.message) {
      displayUserError({
        error,
        title: t('data-tables.error'),
        userMessageFallback: t('data-tables.error')
      })
    }
  }

  //Temporary function
  const placeholderAction = () => undefined

  const getFormattedSelectedRows = () => {
    const formattedSelectedRows: YesObject[] = []

    Object.values(selectedRows).forEach((row) => {
      row.value && formattedSelectedRows.push(row.value)
    })

    return formattedSelectedRows
  }

  const selectAndLinkAction = () => {
    const formattedSelectedRows = getFormattedSelectedRows()
    deselectAllRows()

    selectAndLink(formattedSelectedRows)
  }

  const cancel = () => {
    setMode('view')
    setChangedFieldValues({})
  }

  const formattedExtraSelectionActions = extraSelectionActions?.map(
    (action) => ({
      ...action,
      onClick: () => {
        action.onClick?.(getFormattedSelectedRows())
        deselectAllRows()
      },
      disabled: Object.keys(selectedRows).length === 0
    })
  )

  const selectionActionsOptions = useMemo((): SelectionAction[] => {
    const baseActions = [
      primarySelectionAction || {
        children: t('data-tables.selection-actions'),
        onClick: placeholderAction,
        impactClass: 'situationChange' as const
      }
    ]

    if (!hideEditAction) {
      baseActions.push({
        children: t('data-tables.modify'),
        onClick: () => setMode('edit'),
        impactClass: 'situationChange' as const
      })
    }

    return [...baseActions, ...formattedExtraSelectionActions]
  }, [
    hideEditAction,
    primarySelectionAction,
    t,
    setMode,
    formattedExtraSelectionActions
  ])

  const shouldRenderSelectionActions =
    selectionActionsOptions.length > 1 || !!primarySelectionAction

  const redirectToSelectAndLink =
    objectclass &&
    selectAndLinkOptions &&
    getRedirectToObjectclassCallback({
      targetField: selectAndLinkOptions?.targetField,
      targetGroup: selectAndLinkOptions?.targetGroup,
      objectclassUuid: objectclass?.uuid,
      targetComponent: selectAndLinkOptions?.targetComponent,
      selectedOptions: selectAndLinkOptions.selectedOptions
    })

  const buttonContainer =
    mode === 'edit' || mode === 'insert' ? (
      <div className='col-12 col-md-4'>
        <ButtonList>
          <Button
            onClick={onSave}
            variant='primary'
            impactClass='situationChange'
          >
            {t('data-tables.save-and-activate')}
          </Button>

          <Button onClick={cancel} variant='secondary'>
            {t('data-tables.cancel')}
          </Button>
        </ButtonList>
      </div>
    ) : (
      <ButtonContainer>
        <DocumentListCard>
          <ButtonList>
            {SHOW_SELECT_DESELECT && (
              <>
                <Button
                  heightClass='semiSmall'
                  onClick={placeholderAction}
                  variant='secondary'
                  disabled
                >
                  {t('data-tables.select')}
                </Button>
                <Button
                  heightClass='semiSmall'
                  onClick={placeholderAction}
                  variant='secondary'
                  disabled
                >
                  {t('data-tables.deselect')}
                </Button>
              </>
            )}
            {!isUniselection && (
              <>
                <Button
                  heightClass='semiSmall'
                  onClick={selectAllRows}
                  variant='secondary'
                >
                  {t('data-tables.select-all')}
                </Button>
                <Button
                  heightClass='semiSmall'
                  onClick={deselectAllRows}
                  variant='secondary'
                  disabled={Object.keys(selectedRows).length === 0}
                >
                  {t('data-tables.deselect-all')}
                </Button>
              </>
            )}

            <InsertButton />

            {redirectToSelectAndLink && (
              <Button
                variant='secondary'
                heightClass='semiSmall'
                onClick={redirectToSelectAndLink}
              >
                {t('data-tables.select-and-link')}
              </Button>
            )}

            {shouldRenderSelectionActions && (
              <SyntheticButton
                heightClass='semiSmall'
                options={selectionActionsOptions}
                dropdownLabel='more options'
              />
            )}
            {!disableLinkButton && shouldRenderSelectAndLink() && (
              <Button
                variant='primary'
                heightClass='semiSmall'
                onClick={selectAndLinkAction}
              >
                {t('data-tables.link')}
              </Button>
            )}
          </ButtonList>
        </DocumentListCard>
      </ButtonContainer>
    )

  return <Container>{buttonContainer}</Container>
}

export default TableActions
